import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styles from './login.css';

export default function Login ({onLogin, onSwitchToSignup}) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    // Load remembered credentials on mount
    React.useEffect(() => {
        const rememberedCredentials = localStorage.getItem('rememberedCredentials');
        if (rememberedCredentials) {
            try {
                const {email: savedEmail, username: savedUsername, password: savedPassword, rememberMe: savedRememberMe} = JSON.parse(rememberedCredentials);
                // Fallback to username if email is not saved (migration support)
                setEmail(savedEmail || savedUsername || '');
                setPassword(savedPassword || '');
                setRememberMe(savedRememberMe || false);
            } catch (e) {
                console.error('Error loading remembered credentials:', e);
            }
        }
    }, []);

    const handleSubmit = async e => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            let hwid = '';
            let mac = '';
            try {
                const hwidResponse = await fetch('http://127.0.0.1:14001/get-machine-id', {
                    method: 'GET',
                    signal: AbortSignal.timeout(2000)
                });
                const hwidData = await hwidResponse.json();
                if (hwidData.success && hwidData.hwid) {
                    hwid = hwidData.hwid;
                    mac = hwidData.mac;
                } else {
                    // It's possible it's a free tier user who doesn't need HWID, but we don't know their tier yet.
                    // We will just let hwid be empty and let the backend reject them if they are pro.
                }
            } catch (err) {
                // Agent not reachable. Let hwid be empty, backend will handle rejection for pro users.
            }

            const response = await fetch('https://app.tifstudio.in/api/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({email, password, hwid})
            });

            const data = await response.json();

            if (data.success) {
                // Store user info without usage counts to ensure they are always synced from DB
                const {
                    downloadCount, downloadLimit,
                    uploadCount, uploadLimit,
                    aiUsageCount, aiUsageLimit,
                    ...userWithoutUsage
                } = data.user;

                const userDataToStore = {
                    ...userWithoutUsage,
                    rememberMe: rememberMe
                };
                localStorage.setItem('user', JSON.stringify(userDataToStore));
                if (data.sessionToken) {
                    localStorage.setItem('session_token', data.sessionToken);
                }

                // Save or clear remembered credentials based on rememberMe checkbox
                if (rememberMe) {
                    localStorage.setItem('rememberedCredentials', JSON.stringify({
                        email: email,
                        password: password,
                        rememberMe: true
                    }));
                } else {
                    localStorage.removeItem('rememberedCredentials');
                }

                onLogin();
            } else {
                setError(data.message || 'Invalid email or password');
                setLoading(false);
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Unable to connect to server. Please try again.');
            setLoading(false);
        }
    };

    return (
        <div className={styles.loginPage}>
            <div className={styles.loginBox}>
                <h2 className={styles.brand}>TIF Studio</h2>
                <form onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        {/* <label>Email</label> */}
                        <input
                            type="email"
                            placeholder="Enter email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            disabled={loading}
                            required
                        />
                    </div>


                    <div className={styles.field}>
                        {/* <label>Password</label> */}
                        <div className={styles.passwordWrapper}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                disabled={loading}
                                required
                            />
                            <button
                                type="button"
                                className={styles.togglePassword}
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                    </div>

                    <div className={styles.rememberMe}>
                        <label>
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={e => setRememberMe(e.target.checked)}
                                disabled={loading}
                            />
                            <span>Remember me</span>
                        </label>
                    </div>

                    {error && <div className={styles.error}>{error}</div>}

                    <button
                        className={styles.btn}
                        disabled={loading}
                    >
                        {loading ? <span className={styles.spinner} /> : 'Sign In'}
                    </button>
                </form>
                {/*
                <div className={styles.demo}>
                    <p>Demo credentials</p>
                    <span>admin / admin123</span>
                </div> */}

                <div className={styles.switchLink}>
                    Don't have an account?{' '}
                    <button
                        className={styles.linkBtn}
                        onClick={onSwitchToSignup}
                        type="button"
                    >
                        Sign Up
                    </button>
                </div>
            </div>
        </div>
    );
}

Login.propTypes = {
    onLogin: PropTypes.func.isRequired,
    onSwitchToSignup: PropTypes.func.isRequired
};
